import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useMediaQuery } from '@mui/material';
import Carousel from '../Carousel';
import ImageRow from './ImageRow';

import { ProcessedImage } from 'lib/getImages';

type ImageCarouselProps = {
  images?: ProcessedImage[];
  onImageClick?: (index: number) => void;
  className?: string;
  gap?: number;
  key?: string;
};

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images = [],
  onImageClick,
  className,
  gap = 0,
  key,
}) => {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1279px)');
  const isDesktop = useMediaQuery('(min-width: 1280px)');

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const getContainerHeight = () => {
    if (isMobile) return 333;
    if (isTablet) return 352;
    if (isDesktop) return 445;
    return 333; // Default to mobile height
  };

  const getScaledWidth = (
    naturalWidth: number,
    naturalHeight: number,
    containerHeight: number
  ) => {
    // If width or height is missing, use 4:3 aspect ratio
    if (!naturalWidth || !naturalHeight) {
      return Math.round(containerHeight * (4 / 3));
    }
    const aspectRatio = naturalWidth / naturalHeight;
    if (isNaN(aspectRatio)) return 0;
    return Math.round(containerHeight * aspectRatio);
  };

  const containerHeight = getContainerHeight();

  // Calculate total width of all images (scaled) plus total gap
  const scaledWidths = images.map((img) =>
    getScaledWidth(img.width, img.height, containerHeight)
  );
  const totalGap = gap * (images.length - 1);
  const totalImagesWidth = scaledWidths.reduce((a, b) => a + b, 0);
  const totalWidth = totalImagesWidth + totalGap;

  // If all images fit, render them as BlurredBgImage in a flex row
  if (containerWidth > 0 && totalWidth <= containerWidth) {
    return (
      <div
        className={className}
        style={{
          height: containerHeight,
          position: 'relative',
          overflow: 'hidden',
        }}
        ref={containerRef}
      >
        <ImageRow
          images={images}
          scaledWidths={scaledWidths}
          containerHeight={containerHeight}
          gap={gap}
          onImageClick={onImageClick}
        />
      </div>
    );
  }

  // Otherwise, use the carousel as before
  return (
    <div
      ref={containerRef}
      style={{ height: containerHeight }}
      className={className}
    >
      <Carousel
        key={key || images.length} // Force re-render when images change
        className="w-full h-full"
        emblaOptions={{
          loop: true,
          duration: 20,
          align: 'start',
          slidesToScroll: 'auto',
          containScroll: 'keepSnaps',
          startIndex: 0,
        }}
      >
        {images.map((image, index) => {
          const scaledWidth = getScaledWidth(
            image.width,
            image.height,
            containerHeight
          );
          return (
            <div
              key={index}
              onClick={() => onImageClick?.(index)}
              style={{
                width: `${scaledWidth}px`,
                height: `${containerHeight}px`,
                flexShrink: 0,
                marginRight: gap,
                position: 'relative',
              }}
              className="inline-block align-top cursor-pointer"
            >
              <Image
                src={image.url}
                alt={image.alt}
                fill
                priority={index < 5}
                className="max-w-none"
                loading="eager"
                unoptimized
                blurDataURL={image.blurDataURL}
                style={{ objectFit: 'cover' }}
              />
            </div>
          );
        })}
      </Carousel>
    </div>
  );
};

export default ImageCarousel;
